import { useQueryClient } from "@tanstack/react-query";
import FullPageLoader from "js/components/FullPageLoader";
import MonitoringClassroom from "js/components/MonitoringClassroom";
import { RegularClassroom } from "js/components/RegularClassroom";
import { useClassroomDetailsQuery } from "@/query/classroomDetailsQuery";
import eventService from "js/services/eventService";
import useStore from "stores/index";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { classroomActiveInfoV2 } from "js/utils/classroomSessionUtils";
import p2pManager, { getChannelId } from "js/utils/p2pManager";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAsyncFn, useInterval, useMount, useUnmount } from "react-use";
import shallow from "zustand/shallow";
import * as api from "../../api/screenshotsApi";
import { datadogLogs } from "@datadog/browser-logs";
import { useClassroomPolicyStore } from "stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { useClassroomScreenshotDataStore } from "stores/v2/classroomScreenshotDataStore";
import { datadogRum } from "@datadog/browser-rum";
import { invalidateStudentClaimsQuery } from "@/query/studentClaimsQuery";
import { transformScreenshotsResponse } from "./transformScreenshotsResponse";
import { useTranslation } from "react-i18next";

const SCREENSHOTS_TIMEOUT = 15000;
const SCREENSHOTS_INTERVAL = 30000;

const SCREENSHOT_SUMMARY_LOG_INTERVAL = 120000; // 2 minutes
const MAX_SCREENSHOT_SUMMARY_LOGS = 5;
const SCREENSHOT_SUMMARY_MAX_TIME_LIMIT = SCREENSHOT_SUMMARY_LOG_INTERVAL * MAX_SCREENSHOT_SUMMARY_LOGS;

const ClassroomWindowsV2 = () => {
	const { t } = useTranslation();
	const params = useParams();
	const queryClient = useQueryClient();

	const deviceId = useUserSessionStore((state) => state.active_device);
	const teacherId = useUserSessionStore((state) => state.active_username);

	const classroomStore = useStore(
		(state) => ({
			getClassroomV2: state.classroom.getClassroomV2,
			isClassSessionActiveV2: state.classroom.isClassSessionActiveV2,
			fetchClassrooms: state.classroom.fetchClassrooms,
			updateClassroomDataV2: state.classroom.updateClassroomDataV2,
			setClassDetailsLoaded: state.classroom.setClassDetailsLoaded,
		}),
		shallow
	);
	const selectedClassroomStore = useSelectedClassroomStore(
		(state) => ({
			setData: state.setData,
			reset: state.reset,
		}),
		shallow
	);
	const activeClassroomWindowStore = useStore((state) => ({ setData: state.activeClassroomWindow.setData }), shallow);
	const setStudentWindowData = useClassroomScreenshotDataStore((state) => state.setStudentWindowData);

	// purposefully only contains a small amount of the classroom info, use classroomQuery.data for full object. EDU-4354 should remove the requirement for this state.
	const [classroom, setClassroom] = useState(
		_.pick(classroomStore.getClassroomV2(params.classId), ["id", "stableIdV1", "type", "sourceId", "name"])
	);

	const [isClassActive, setClassActive] = useState(classroomStore.isClassSessionActiveV2(params.classId));

	const screenshotsMap = useRef(new Map());
	const classroomQuery = useClassroomDetailsQuery(
		params.classId,
		true,
		true,
		false,
		classroom?.type,
		classroom?.sourceId,
		useUserSessionStore.getState().getTeacherId(),
		{
			enabled: classroom && !_.isEmpty(classroom),
			select: (data) => data.classroom,
			onSuccess: (classroom) => {
				// initialize screenshot object for users without screenshots (first fetch or user added)
				const _pendingScreenshotsUsers = new Map();
				classroom.students?.forEach((student) => {
					if (!screenshotsMap.current.has(student.username)) {
						_pendingScreenshotsUsers.set(student.username, makeScreenshotRequest(student.username, null));
						screenshotsMap.current.set(student.username, {
							screenshots: [],
							username: student.username,
							fname: student.firstName,
							lname: student.lastName,
							stableIdV1: student.stableIdV1,
							qoriaUserId: student.qoriaUserId,
						});
					}
				});

				// remove screenshots of students who have been removed from the class
				const removedUsers = _.differenceBy([...screenshotsMap.current.values()], classroom.students, "username");
				removedUsers.forEach((user) => {
					screenshotsMap.current.delete(user.username);
				});

				fetchScreenshotsWrapper(_pendingScreenshotsUsers);
			},
		}
	);

	useScreenshotsSummaryLogging(isClassActive, screenshotsMap, classroom?.name);

	// fetch screenshots function
	const backloggedScreenshotRequestsRef = useRef(new Map());
	const [screenshotsResponse, fetchScreenshots] = useAsyncFn(async (screenshotRequestsMap) => {
		if (screenshotRequestsMap.length === 0) {
			return null;
		}

		const applianceId = useUserSessionStore.getState().getActiveDevice();
		if (!applianceId) {
			return null;
		}
		const data = await api.getStudentScreenshots([...screenshotRequestsMap.values()], applianceId, SCREENSHOTS_TIMEOUT);
		if (!data || data.length === 0) {
			return null;
		}
		backloggedScreenshotRequestsRef.current.clear();

		// update the screenshots map with new screenshots
		const transformed = transformScreenshotsResponse(data.result);
		transformed.forEach((transformedScreenshotUser) => {
			const match = screenshotsMap.current.get(transformedScreenshotUser.username);
			match.screenshots = transformedScreenshotUser.screenshots;
			// Update zustand store for this student
			setStudentWindowData(transformedScreenshotUser.username, transformedScreenshotUser.screenshots || []);
		});

		return null;
	});

	useEffect(() => {
		if (classroomQuery.data) {
			selectedClassroomStore.setData({ students: classroomQuery.data.students });
		}
	}, [classroomQuery.data, selectedClassroomStore]);

	useEffect(() => {
		if (screenshotsResponse.error) {
			console.error("Error fetching screenshots", screenshotsResponse.error);
		}
	}, [screenshotsResponse.error]);

	/**
	 * Calls fetchScreenshots if a fetch is not in progress, otherwise adds to a backlog to be processed on the next fetch
	 */
	const fetchScreenshotsWrapper = useCallback(
		async (screenshotRequestsMap) => {
			if (screenshotRequestsMap.size === 0) {
				return;
			}

			/**
			 * combines the two maps, and ensures the trace context is preserved for items present in both maps
			 */
			function combinePreservingTrace(currentMap, backlogMap) {
				const combined = new Map();
				const usernames = new Set([...currentMap.keys(), ...backlogMap.keys()]);
				for (const username of usernames) {
					const current = currentMap.get(username);
					const backlogged = backlogMap.get(username);
					if (backlogged?.trace && current?.trace) {
						// backlogged request and current request have trace, use current as it's more recent
						combined.set(username, current);
					} else if (current?.trace) {
						// only current request has trace
						combined.set(username, current);
					} else if (backlogged?.trace) {
						// only backlogged request has trace
						combined.set(username, backlogged);
					} else if (current || backlogged) {
						// no trace, doesn't matter which one we use, use whichever is defined
						combined.set(username, current || backlogged);
					} else {
						// no request for this username, this shouldn't happen
						console.error("Invariant failure: couldn't find screenshot request for username", username);
					}
				}
				return combined;
			}

			if (screenshotsResponse.loading) {
				const combined = combinePreservingTrace(screenshotRequestsMap, backloggedScreenshotRequestsRef.current);
				backloggedScreenshotRequestsRef.current = combined;
			} else {
				const combined = combinePreservingTrace(screenshotRequestsMap, backloggedScreenshotRequestsRef.current);
				fetchScreenshots(combined);
			}
		},
		[screenshotsResponse.loading, fetchScreenshots]
	);

	const [classroomPolicyLoaded, setClassroomPolicyLoaded] = useState(false);

	const handleInitP2P = useCallback(() => {
		if (!classroomQuery.data) {
			console.warn("handleInitP2P called before classroomQuery.data was available");
			return;
		}

		const classroom = classroomQuery.data;

		const deviceId = useUserSessionStore.getState().getActiveDevice();
		if (p2pManager.signaler && p2pManager.channel === getChannelId(deviceId, classroom.sourceId)) {
			return;
		}

		const activeClassroom = classroomStore.getClassroomV2(classroom.id);
		if (!Object.entries(activeClassroom).length) {
			return;
		}

		const [, sessionEndTime] = classroomActiveInfoV2(activeClassroom);
		if (sessionEndTime > Date.now() / 1000) {
			p2pManager
				.init(classroom.sourceId, sessionEndTime, activeClassroom["zoom"])
				.catch((err) => console.error("Fatal error initializing P2PManager", err));
		}
	}, [classroomQuery.data, classroomStore]);

	// fetch classrooms if it isn't already fetched - this occurs when the active classroom page is refreshed or visited directly, rather then navigating from classrooms overview
	useMount(() => {
		const fn = async () => {
			let _classroom = classroom;
			if (!isClassroomDataAvailable()) {
				await classroomStore.fetchClassrooms();
				_classroom = classroomStore.getClassroomV2(params.classId);
			}

			setClassroom(_.pick(_classroom, ["id", "stableIdV1", "type", "sourceId", "name"]));
			selectedClassroomStore.setData({
				id: _classroom.id,
				stableIdV1: _classroom.stableIdV1,
				name: _classroom.name,
				sourceId: _classroom.sourceId,
			});
		};
		fn();
	});

	useUnmount(() => {
		selectedClassroomStore.reset();
	});

	useInterval(
		() => {
			const screenshotRequests = new Map();
			classroomQuery.data?.students?.forEach((student) =>
				screenshotRequests.set(student.username, makeScreenshotRequest(student.username, null))
			);
			fetchScreenshotsWrapper(screenshotRequests);
		},
		isClassActive ? SCREENSHOTS_INTERVAL : null
	);

	async function handleClassStartedEvent() {
		const result = await classroomQuery.refetch();

		if (result.data) {
			if (result.data.session && result.data.session.sessionEndTime) {
				// We need to update classroomStore AND set isClassActive to true because they are BOTH used as sources of truth in different places for whether or not the class is active :(
				classroomStore.updateClassroomDataV2({ classroom: result.data });
				const updatedClassroom = classroomStore.getClassroomV2(params.classId);
				const isActive = classroomStore.isClassSessionActiveV2(updatedClassroom);
				setClassActive(isActive);
			} else {
				const errorMsg = "session data missing from classroom details object fetched upon receiving CLASS_STARTED event";
				console.error(errorMsg, result.data);
				datadogRum.addError({ message: errorMsg, data: result.data });
				classroomStore.updateClassroomDataV2({ classroom: result.data });
			}
		} else {
			const errorMsg = "Failed to refetch classroom details object upon receiving CLASS_STARTED event";
			console.error(errorMsg, result.error);
			datadogRum.addError({ message: errorMsg, data: result.error });
		}
	}

	// init event service and listen for screenshots
	useEffect(() => {
		async function handleStudentClaimUpdateEvent(event) {
			const data = JSON.parse(event.data);
			if (!data.affectedClassroomIds?.includes(classroom?.stableIdV1)) {
				return;
			}
			await invalidateStudentClaimsQuery(queryClient, classroom?.stableIdV1);
		}

		function addEventServiceListeners() {
			if (eventService.eventSource) {
				eventService.eventSource.addEventListener("SCREENSHOT_UPDATE", handleScreenshotUpdateEvent);
				eventService.eventSource.addEventListener("CLASS_STARTED", handleClassStartedEvent);
				eventService.eventSource.addEventListener("STUDENT_CLAIM_UPDATE", handleStudentClaimUpdateEvent);
			}
		}

		function removeEventServiceListeners() {
			if (eventService.eventSource) {
				eventService.eventSource.removeEventListener("SCREENSHOT_UPDATE", handleScreenshotUpdateEvent);
				eventService.eventSource.removeEventListener("CLASS_STARTED", handleClassStartedEvent);
				eventService.eventSource.removeEventListener("STUDENT_CLAIM_UPDATE", handleStudentClaimUpdateEvent);
			}
		}

		function handleScreenshotUpdateEvent(event) {
			if (!isClassActive) {
				return;
			}
			const data = JSON.parse(event.data);
			const userId = data.senderUserId;

			const screenshotRequestsMap = new Map();

			// Use classroom state or classroomQuery.data for classroom info
			const classroomName = classroom?.name || classroomQuery.data?.name;

			//Ideally this should never happen based on current architecture, capture if it does
			if (event.type.toLowerCase() === "screenshot_update" && !userId) {
				const errLog = "Screenshot Update Event Error - userId not present in screenshot event";
				datadogLogs.logger.info(errLog, { customer: deviceId, class: classroomName, teacher: teacherId });
			}

			if (event.type.toLowerCase() === "screenshot_update" && userId) {
				const intendedUser = screenshotsMap.current.get(userId);
				if (intendedUser) {
					screenshotRequestsMap.set(intendedUser.username, makeScreenshotRequest(intendedUser.username, data.header?.trace));
				}
			}

			fetchScreenshotsWrapper(screenshotRequestsMap);
		}

		addEventServiceListeners();
		const unsubEventServiceStatus = useStore.subscribe(
			(state) => state.eventServiceStatus.status,
			() => {
				addEventServiceListeners();
			}
		);

		return () => {
			unsubEventServiceStatus();
			removeEventServiceListeners();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryClient, isClassActive, fetchScreenshotsWrapper, params.classId]);

	// actions to run when classroomQuery data is available/updated
	useEffect(() => {
		if (!classroomQuery.data) {
			return;
		}

		const { id: classroomId } = classroomQuery.data;

		useClassroomPolicyStore
			.getState()
			.fetchPolicyAndPopulateStore({})
			.then(() => {
				setClassroomPolicyLoaded(true);
			})
			.catch((err) => {
				console.error("Error fetching classroom policy", err);
			});

		classroomStore.updateClassroomDataV2({ classroom: classroomQuery.data });
		classroomStore.setClassDetailsLoaded(true);
		setClassActive(classroomStore.isClassSessionActiveV2(classroomId));

		const unsubClassroomExpiry = useStore.subscribe(
			(state) => state.activeClassroomWindow.classroomExpiry,
			(expiry, previousExpiry) => {
				if (expiry.classroomId === classroomQuery.data?.sourceId && expiry.expiry !== previousExpiry.expiry) {
					setClassActive(classroomStore.isClassSessionActiveV2(classroomId));
					handleInitP2P();
				}
			}
		);

		const interval = setInterval(() => {
			setClassActive(classroomStore.isClassSessionActiveV2(classroomId));
		}, 10000);

		return () => {
			unsubClassroomExpiry();
			clearInterval(interval);
		};
	}, [activeClassroomWindowStore, classroomQuery.data, classroomStore, handleInitP2P]);

	useEffect(() => {
		handleInitP2P();
	}, [handleInitP2P]);

	const isClassroomDataAvailable = () => {
		const currentClassroom = classroomStore.getClassroomV2(params.classId);
		return Object.entries(currentClassroom).length > 0;
	};

	const renderMonitoring = () => {
		const personalisation = classroomStore.getClassroomV2(classroomQuery.data.id).personalisation;
		return <MonitoringClassroom users={[...screenshotsMap.current.values()]} classroom={{ ...classroomQuery.data, personalisation }} />;
	};

	const renderClassroom = () => {
		const personalisation = classroomStore.getClassroomV2(classroomQuery.data.id).personalisation;
		return (
			<RegularClassroom
				users={[...screenshotsMap.current.values()]}
				classroom={{ ...classroomQuery.data, personalisation }}
				isClassStarted={isClassActive}
			/>
		);
	};

	const render = () => {
		if (!classroomPolicyLoaded || !classroomQuery.data) {
			return <FullPageLoader message={t("classroomDetails.loadingClassroom")} />;
		}

		return classroomQuery.data.session?.isMonitoring ? renderMonitoring() : renderClassroom();
	};

	return render();
};

/**
 * Logs a summary of the screenshots data on an interval controlled by SCREENSHOT_SUMMARY_LOG_INTERVAL and MAX_SCREENSHOT_SUMMARY_LOGS
 */
const useScreenshotsSummaryLogging = (isClassActive, screenshotsMap, classroomSourceName) => {
	const [screenshotSummaryCaptureCounter, setScreenshotSummaryCaptureCounter] = useState(0);
	const [classOpenTime, setClassOpenTime] = useState(null);

	useEffect(() => {
		if (isClassActive) {
			setScreenshotSummaryCaptureCounter(0);
			setClassOpenTime(Date.now());
		} else {
			setClassOpenTime(null);
		}
	}, [isClassActive]);

	useInterval(
		() => {
			const secondsSinceOpen = _.round((Date.now() - classOpenTime) / 1000, -1);

			if (secondsSinceOpen > SCREENSHOT_SUMMARY_MAX_TIME_LIMIT) {
				setScreenshotSummaryCaptureCounter(MAX_SCREENSHOT_SUMMARY_LOGS);
				return;
			}

			const studentCount = screenshotsMap.current.size;
			const studentsWithoutScreenshots = Array.from(screenshotsMap.current.values()).filter((student) => student.screenshots.length === 0);
			const studentsWithScreenshotsPercent = Math.round(((studentCount - studentsWithoutScreenshots.length) / studentCount) * 100);
			const numUsernamesToDisplay = Math.min(studentsWithoutScreenshots.length, 5);

			let studentsWithoutScreenshotsString = studentsWithoutScreenshots
				.slice(0, numUsernamesToDisplay)
				.map((student) => student.username)
				.join(",");

			if (studentsWithoutScreenshots.length > 5) {
				studentsWithoutScreenshotsString += ` [+${studentsWithoutScreenshots.length - 5} more]`;
			}

			const logLine = `active classroom "${classroomSourceName}" screenshots summary. since_open_seconds=${secondsSinceOpen} total_students=${studentCount} students_with_screenshots=${studentsWithScreenshotsPercent}% students_without_screenshots=${studentsWithoutScreenshotsString}`;

			console.log(logLine);
			datadogLogs.logger.info(logLine, {
				students_with_screenshots_percent: studentsWithScreenshotsPercent,
				total_students: studentCount,
				since_open: secondsSinceOpen,
			});

			setScreenshotSummaryCaptureCounter(screenshotSummaryCaptureCounter + 1);
		},
		isClassActive && classOpenTime && screenshotsMap.current && screenshotSummaryCaptureCounter < MAX_SCREENSHOT_SUMMARY_LOGS
			? SCREENSHOT_SUMMARY_LOG_INTERVAL
			: null
	);
};

export function makeScreenshotRequest(username, trace) {
	return { username, trace };
}

export default ClassroomWindowsV2;
